import { useMemo } from "react";

function numOrEmpty(v) {
  if (v === "" || v === null || typeof v === "undefined") return "";
  const n = Number(v);
  return Number.isFinite(n) ? n : "";
}

function wholeStock(value) {
  const quantity = Number(value);
  if (!Number.isFinite(quantity) || quantity <= 0) return 0;
  return Math.floor(quantity);
}

function stockStatus(variant, threshold) {
  const quantity = wholeStock(variant?.quantity);
  const parsedThreshold = Number(threshold);
  const lowStockThreshold = threshold !== "" && threshold != null && Number.isFinite(parsedThreshold) && parsedThreshold >= 0
    ? parsedThreshold
    : 5;

  if (!variant?.id && quantity === 0) {
    return {
      label: "No stock yet",
      className: "border-zinc-200 bg-zinc-50 text-zinc-600 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400",
    };
  }

  if (quantity === 0) {
    return {
      label: "Out of Stock",
      className: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/50 dark:text-rose-300",
    };
  }

  if (quantity <= lowStockThreshold) {
    return {
      label: "Low Stock",
      className: "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/50 dark:text-amber-300",
    };
  }

  return {
    label: "In Stock",
    className: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/50 dark:text-emerald-300",
  };
}

export default function StepInventory({ state, setState }) {
  const lowStockThreshold = state.inventory?.lowStockThreshold ?? "";
  const variants = useMemo(() => state.variants || [], [state.variants]);
  const hasVariants = variants.length > 0;

  const setInv = (patch) => {
    setState((p) => ({
      ...p,
      inventory: { ...(p.inventory || {}), ...patch },
    }));
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-[#121214]">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              Inventory Overview
            </div>
            <div className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
              Current on-hand stock comes from procurement receipts and inventory operations.
            </div>
          </div>

          <span className="inline-flex items-center rounded-full border border-zinc-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-zinc-700 dark:border-zinc-800 dark:bg-transparent dark:text-zinc-300">
            {hasVariants ? `${variants.length} variant${variants.length > 1 ? "s" : ""}` : "No variants"}
          </span>
        </div>
      </div>

      {hasVariants && (
        <div className="rounded-2xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-[#121214]">
          <div className="border-b border-zinc-200 p-4 dark:border-zinc-800">
            <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              Variant Inventory
            </div>
            <div className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
              Existing variants show their current stock. Newly created variants start at 0 until stock is received or adjusted.
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-xs text-zinc-600 dark:bg-[#0f0f10] dark:text-zinc-400">
                <tr>
                  <th className="p-3 text-left">Variant</th>
                  <th className="p-3 text-left">SKU</th>
                  <th className="p-3 text-right">On Hand</th>
                  <th className="p-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
                {variants.map((v, idx) => {
                  const title = Object.values(v.optionValues || {}).join(" / ") || `Variant ${idx + 1}`;
                  const sku = (v.sku || "").trim();
                  const quantity = wholeStock(v.quantity);
                  const status = stockStatus(v, lowStockThreshold);

                  return (
                    <tr key={idx} className="bg-white dark:bg-[#121214]">
                      <td className="p-3 font-medium text-zinc-900 dark:text-zinc-100">{title}</td>
                      <td className="p-3 font-mono text-xs text-zinc-600 dark:text-zinc-400">{sku || "-"}</td>
                      <td className="p-3 text-right tabular-nums text-zinc-900 dark:text-zinc-100">{quantity}</td>
                      <td className="p-3">
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${status.className}`}>
                          {status.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-[#121214]">
        <div className="mb-3 text-sm font-semibold text-zinc-900 dark:text-zinc-100">
          Low Stock Threshold
        </div>
        <div className="space-y-3">
          <div className="text-xs text-zinc-600 dark:text-zinc-400">
            Configure when stock warnings should appear for this product after operational stock has been received.
          </div>

          <input
            type="number"
            min={0}
            step={1}
            value={numOrEmpty(lowStockThreshold)}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "" || raw === null) {
                setInv({ lowStockThreshold: "" });
                return;
              }
              const n = Number(raw);
              if (!Number.isFinite(n) || n < 0) return;
              setInv({ lowStockThreshold: Math.floor(n) });
            }}
            onKeyDown={(e) => {
              if (e.key === "." || e.key === "-" || e.key === "e") e.preventDefault();
            }}
            placeholder="e.g. 5"
            className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm dark:border-zinc-800 dark:bg-transparent"
          />
        </div>
      </div>

      <div className="rounded-2xl border border-blue-100 bg-blue-50/50 p-4 dark:border-blue-900/30 dark:bg-blue-950/20">
        <div className="text-sm font-semibold text-blue-900 dark:text-blue-100">
          How does stock work?
        </div>
        <div className="mt-2 space-y-1 text-xs text-blue-800 dark:text-blue-200">
          <p>Stock is managed through the procurement pipeline:</p>
          <ol className="ml-4 list-decimal space-y-0.5">
            <li>Create a <b>Purchase Requisition</b> for items you need</li>
            <li>Send <b>RFQs</b> to suppliers and collect quotes</li>
            <li>Accept a quote to create a <b>Purchase Order</b></li>
            <li>When goods arrive, <b>receive stock</b> in the Inventory tab</li>
          </ol>
          <p className="mt-2 text-blue-700 dark:text-blue-300">
            This keeps stock changes operationally tracked and auditable.
          </p>
        </div>
      </div>

      {!hasVariants && (
        <div className="rounded-2xl border border-zinc-200 bg-white p-4 text-sm text-zinc-600 dark:border-zinc-800 dark:bg-[#121214] dark:text-zinc-400">
          No variants found. Add options and generate variants first to see the inventory overview.
        </div>
      )}
    </div>
  );
}
